import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Crown, User, Calendar, CheckCircle, XCircle } from 'lucide-react';

interface VIPRequest {
  id: string;
  promoterName: string;
  promoterCompany: string;
  requestedBy: string;
  requestedDate: string;
  reason: string;
  prospectName: string;
  prospectCompany: string;
}

interface VIPApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: VIPRequest;
  onApprove: (requestId: string) => void;
  onReject: (requestId: string, reason: string) => void;
}

const VIPApprovalModal: React.FC<VIPApprovalModalProps> = ({
  isOpen,
  onClose,
  request,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  const [rejectReason, setRejectReason] = React.useState('');
  const [showRejectForm, setShowRejectForm] = React.useState(false);

  if (!isOpen) return null;

  const handleApprove = () => {
    onApprove(request.id);
    onClose();
  };

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(request.id, rejectReason);
      setRejectReason('');
      setShowRejectForm(false);
      onClose();
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">{t('promoters.vipApproval.title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Crown className="w-5 h-5 text-yellow-600" />
                <h3 className="font-semibold text-yellow-900">{t('promoters.vipApproval.detailsTitle')}</h3>
              </div>
              <p className="text-sm text-yellow-800">
                {t('promoters.vipApproval.detailsDesc')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <User className="w-5 h-5 text-gray-600" />
                  <h4 className="font-semibold text-gray-900">{t('promoters.vipApproval.vipPromoter')}</h4>
                </div>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">{t('promoters.vipApproval.name')}</p>
                    <p className="font-medium text-gray-900">{request.promoterName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('promoters.vipApproval.company')}</p>
                    <p className="font-medium text-gray-900">{request.promoterCompany}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <User className="w-5 h-5 text-gray-600" />
                  <h4 className="font-semibold text-gray-900">{t('promoters.vipApproval.forProspect')}</h4>
                </div>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">{t('promoters.vipApproval.name')}</p>
                    <p className="font-medium text-gray-900">{request.prospectName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('promoters.vipApproval.company')}</p>
                    <p className="font-medium text-gray-900">{request.prospectCompany}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Calendar className="w-5 h-5 text-gray-600" />
                <h4 className="font-semibold text-gray-900">{t('promoters.vipApproval.requestInfo')}</h4>
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-gray-500">{t('promoters.vipApproval.requestedBy')}</p>
                  <p className="font-medium text-gray-900">{request.requestedBy}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t('promoters.vipApproval.requestDate')}</p>
                  <p className="font-medium text-gray-900">{request.requestedDate}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('promoters.vipApproval.reasonForRequest')}</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700">{request.reason}</p>
              </div>
            </div>

            {showRejectForm && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('promoters.vipApproval.rejectionReasonLabel')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder={t('promoters.vipApproval.rejectionPlaceholder')}
                />
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              {!showRejectForm ? (
                <>
                  <button
                    onClick={() => setShowRejectForm(true)}
                    className="flex items-center space-x-2 px-4 py-2 text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                    <span>{t('promoters.vipApproval.reject')}</span>
                  </button>
                  <button
                    onClick={handleApprove}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span>{t('promoters.vipApproval.approve')}</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setShowRejectForm(false);
                      setRejectReason('');
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={!rejectReason.trim()}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle className="w-5 h-5" />
                    <span>{t('promoters.vipApproval.confirmRejection')}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VIPApprovalModal;
